"use client";

import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

import { convertRecordingToUploadFile } from "@/components/fileCoverInput/inputMethods/fileUpload/convertRecordingToUploadFile";
import { getLostRecordingSeconds } from "@/components/fileCoverInput/inputMethods/fileUpload/getLostRecordingSeconds";
import { handleFileUpload } from "@/components/fileCoverInput/inputMethods/fileUpload/handleFileUpload";
import { watchMicrophoneTrack } from "@/components/fileCoverInput/inputMethods/fileUpload/watchMicrophoneTrack";
import { NormalText } from "@/components/text/NormalText";
import { SmallTitle } from "@/components/text/SmallTitle";
import { useSetAuthModalOpen } from "@/lib/clerk/useSetAuthModalOpen";
import { AudioInputState, UserNoDates } from "@/types/types";
import { classNames } from "@/utils/classNames";

// Long enough to show a warning the user has time to read
const MIC_WARNING_TOAST_DURATION_MS = 10000;

export function AudioRecorderUser({
  user,
  checkSubscription,
  setInvalidLengthSubscriptionModalOpen,
  audioLengthMinLimitMinutes,
  audioLengthMaxLimitMinutes,
  setInputFiles,
  currentAudioLengthS,
  className,
}: {
  user: UserNoDates | null;
  checkSubscription: boolean;
  setInvalidLengthSubscriptionModalOpen: (value: boolean) => void;
  audioLengthMinLimitMinutes: number | null;
  audioLengthMaxLimitMinutes: number;
  setInputFiles: AudioInputState["setInputFile"][];
  currentAudioLengthS: number;
  className?: string;
}) {
  const setAuthModalOpen = useSetAuthModalOpen();
  const [isRecording, setIsRecording] = useState(false);
  // The elapsed time travels with the blob so the gap check compares the take's own wall-clock span against its decoded audio
  const [recording, setRecording] = useState<{
    blob: Blob;
    elapsedS: number;
  } | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopWatchingMicrophoneRef = useRef<(() => void) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Cleanup function to stop all media tracks and prevent memory leaks
  const stopAllTracks = () => {
    if (stopWatchingMicrophoneRef.current !== null) {
      stopWatchingMicrophoneRef.current();
      stopWatchingMicrophoneRef.current = null;
    }
    if (streamRef.current !== null) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }
  };

  const stopMediaRecorder = () => {
    if (
      mediaRecorderRef.current !== null &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  };

  const toggleRecording = async () => {
    setError(null);

    if (isRecording) {
      stopMediaRecorder();
      // CRITICAL: Stop all media tracks to prevent memory leaks
      stopAllTracks();
    } else {
      if (user === null) {
        setAuthModalOpen();
        return;
      }

      // Clean up any lingering streams before starting new recording
      stopAllTracks();

      // Start recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        streamRef.current = stream; // Store stream reference for cleanup
        const stopWatchers = stream.getAudioTracks().map((audioTrack) =>
          watchMicrophoneTrack(audioTrack, {
            onEnded: () => {
              stopMediaRecorder();
              stopAllTracks();
              toast.warning(
                "Your microphone disconnected, so the recording was stopped. The part recorded so far was kept.",
                { duration: MIC_WARNING_TOAST_DURATION_MS }
              );
            },
            onSustainedMute: () => {
              toast.warning(
                "Your microphone stopped sending audio. Check that it is connected and not muted.",
                { duration: MIC_WARNING_TOAST_DURATION_MS }
              );
            },
          })
        );
        stopWatchingMicrophoneRef.current = () => {
          stopWatchers.forEach((stopWatcher) => stopWatcher());
        };
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        // A closure constant rather than a ref: the only reader is the onstop handler created right here, and it can only run after start()
        const startedAtMs = Date.now();
        mediaRecorderRef.current.onstop = () => {
          const elapsedS = (Date.now() - startedAtMs) / 1000;
          // The chunks keep the browser's own container type; convertRecordingToUploadFile normalizes the format before upload
          const blob = new Blob(audioChunksRef.current);
          setRecording({ blob, elapsedS });
          mediaRecorderRef.current = null; // Clear ref after blob creation
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);
      } catch (err) {
        console.error("Error accessing audio devices: ", err);
        setError("Error accessing audio devices.");
        // Clean up stream on error
        stopAllTracks();
      }
    }
  };

  useEffect(() => {
    if (recording === null) {
      return;
    }

    const uploadRecording = async () => {
      let recordedFile: File;
      try {
        const { uploadFile, decodedDurationS } =
          await convertRecordingToUploadFile(recording.blob);
        recordedFile = uploadFile;

        const lostSeconds = getLostRecordingSeconds(
          recording.elapsedS,
          decodedDurationS
        );
        if (lostSeconds !== null) {
          toast.warning(
            `Your microphone stopped sending audio for about ${lostSeconds} seconds. The recording is ${Math.round(decodedDurationS)} seconds long. If part of it is missing, check your microphone and record again.`,
            { duration: MIC_WARNING_TOAST_DURATION_MS }
          );
        }
      } catch (conversionError) {
        console.error(
          "Failed to encode recording for upload:",
          conversionError
        );
        // Same contract as captureAudioLengthException in getAudioLengthClient: a browser that cannot decode its own recording is worth knowing about, but telemetry must never break the recorder
        try {
          posthog.captureException(conversionError, {
            recordingBytes: recording.blob.size,
            recordingType: recording.blob.type,
          });
        } catch (telemetryError) {
          console.warn("Failed to track recording error:", telemetryError);
        }
        setError("Could not process the recording. Please try again.");
        setIsRecording(false);
        return;
      }

      handleFileUpload({
        files: [recordedFile],
        user,
        setAuthModalOpen,
        checkSubscription,
        setInvalidLengthSubscriptionModalOpen,
        audioLengthMinLimitMinutes,
        audioLengthMaxLimitMinutes,
        setInputFiles,
        currentAudioLengthS,
        setError,
        maxMultiAudioLengthMinutes: audioLengthMaxLimitMinutes,
      });

      setIsRecording(false); // Reset recording state after upload
    };

    void uploadRecording();
  }, [recording]);

  // Cleanup on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      stopAllTracks();
      stopMediaRecorder();
      mediaRecorderRef.current = null;
    };
  }, []);

  return (
    <button
      data-testid="audio-recorder-button"
      className={twMerge(
        "relative flex w-full flex-col items-center justify-center rounded-3xl border-2 p-4 transition-colors",
        className,
        error !== null ? "border-red-500" : (
          "border-gray-900/25 hover:border-gray-900/10"
        )
      )}
      onClick={toggleRecording}
      type="button"
    >
      <div
        className={classNames(
          isRecording ? "border-red-600" : "border-gray-900",
          "mb-2 h-5 w-5 rounded-full border-2 p-[2px]"
        )}
      >
        <div
          className={classNames(
            isRecording ? "bg-red-600" : "bg-transparent",
            "h-full w-full rounded-full"
          )}
        />
      </div>
      <SmallTitle elementType="p">
        {isRecording ? "Stop recording" : "Record"}
      </SmallTitle>

      {error !== null && (
        <NormalText
          elementType="p"
          className="absolute -bottom-4 left-0 h-2 leading-4 text-red-500"
        >
          {error}
        </NormalText>
      )}
    </button>
  );
}
